import type { TableProps as MuiTableProps } from '@mui/material';
import type {
  Row,
  RowData,
  Table as TanstackTable,
} from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import type { ComponentProps, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '../table/table';
import {
  dataTableMetaToCellProps,
  getDataTableColumnMeta,
} from './dataTableMetaProps';
import {
  DataTableTablePaginationFooter,
  type DataTableTablePaginationOptions,
} from './dataTablePaginationFooter';
import { DataTableSkeletonTableBody } from './dataTableSkeleton';
import './dataTable.types';

export type { DataTableColumnMeta } from './dataTable.types';

import { Box } from 'hephaestus/web';

export type DataTableGetRowProps<TData extends RowData> = (
  row: Row<TData>,
) => Omit<ComponentProps<typeof TableRow>, 'ref'> | undefined;

export type DataTableViewSharedProps<TData extends RowData> = {
  /**
   * TanStack table instance. On the design-system `DataTable` wrapper, this property (vs. `config`)
   * discriminates the two supported prop shapes at compile time.
   */
  table: TanstackTable<TData>;
  /**
   * Current page size from the consumer (e.g. `pagination.pageSize` from `useDataTable`).
   * When `loading` is true, the body renders this many skeleton rows; otherwise it drives
   * layout expectations next to TanStack pagination. Typically one of the `ROWS_PER_PAGE` presets.
   */
  rowsPerPage: number;
  /** When true, the body shows `rowsPerPage` skeleton rows instead of data (header unchanged). */
  loading?: boolean;
  /** Shown when `table.getRowModel().rows.length === 0`. */
  emptyContent?: ReactNode;
  /** Passed to the empty row’s single cell. Defaults to all leaf columns. */
  emptyColSpan?: number;
  /** Extra props for the root {@link Table}. */
  tableProps?: MuiTableProps;
  /** Customize body rows (hover, click, test ids, etc.). */
  getRowProps?: DataTableGetRowProps<TData>;
  /** `data-testid` on the root table element. */
  testId?: string;
  /**
   * Renders design-system {@link Pagination} below the table (TanStack client mode or
   * fully controlled page handlers for server pagination).
   */
  tablePagination?: DataTableTablePaginationOptions<TData>;
};

export type DataTableViewProps<TData extends RowData> =
  DataTableViewSharedProps<TData>;

export type {
  DataTableControlledPaginationOptions,
  DataTableTablePaginationOptions,
  DataTableTanstackPaginationOptions,
} from './dataTablePaginationFooter';

/**
 * Design-system table “skin”: maps TanStack header groups and rows to MUI table
 * primitives. Prefer {@link DataTable} or {@link useDataTable} in feature code —
 * this export exists for typed composition and tests.
 */
export function DataTableView<TData extends RowData>(
  props: DataTableViewProps<TData>,
): JSX.Element {
  const {
    table,
    rowsPerPage,
    loading = false,
    emptyContent,
    emptyColSpan,
    tableProps,
    getRowProps,
    testId,
    tablePagination,
  } = props;
  const resolvedSkeletonRows = loading
    ? Math.max(1, Math.floor(rowsPerPage))
    : 0;
  const leafColumnCount = table.getAllLeafColumns().length;
  const resolvedEmptyColSpan = emptyColSpan ?? leafColumnCount;

  const tableSx = [
    { overflow: 'visible' },
    ...(Array.isArray(tableProps?.sx)
      ? tableProps.sx
      : tableProps?.sx != null
        ? [tableProps.sx]
        : []),
  ];

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box
        data-testid='data-table-horizontal-scroll'
        sx={{
          width: '100%',
          minWidth: 0,
          overflowX: 'auto',
          overflowY: 'visible',
        }}
      >
        <Table data-testid={testId} {...tableProps} sx={tableSx}>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const meta = getDataTableColumnMeta(header.column);
                  const sortDisabled = Boolean(
                    meta?.disableDataTableSortHandling,
                  );
                  const canSort = header.column.getCanSort() && !sortDisabled;
                  const sorted = header.column.getIsSorted();

                  return (
                    <TableHeadCell
                      key={header.id}
                      colSpan={header.colSpan}
                      {...dataTableMetaToCellProps(meta)}
                      headLabelVariant={meta?.headerLabelVariant}
                      headLabelWeight={meta?.headerLabelWeight}
                      headLabelSx={meta?.headerLabelSx}
                      textColor={meta?.headerTextColor}
                      headerBare={meta?.headerBare}
                      isSortable={canSort}
                      sortDirection={
                        sorted === false
                          ? undefined
                          : (sorted as 'asc' | 'desc')
                      }
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHeadCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          {loading ? (
            <DataTableSkeletonTableBody
              rowCount={resolvedSkeletonRows}
              columns={table.getVisibleLeafColumns()}
              testId={testId}
            />
          ) : (
            <TableBody>
              {table.getRowModel().rows.length === 0 &&
              emptyContent !== undefined ? (
                <TableRow>
                  <TableCell colSpan={resolvedEmptyColSpan}>
                    {emptyContent}
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => {
                  const rowProps = getRowProps?.(row);
                  return (
                    <TableRow key={row.id} {...rowProps}>
                      {row.getVisibleCells().map((cell) => {
                        const meta = getDataTableColumnMeta(cell.column);
                        return (
                          <TableCell
                            key={cell.id}
                            {...dataTableMetaToCellProps(meta)}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          )}
        </Table>
      </Box>
      {tablePagination !== undefined && (
        <DataTableTablePaginationFooter
          table={table}
          options={tablePagination}
          loading={loading}
        />
      )}
    </Box>
  );
}
